import React, { useState, useEffect } from 'react';
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui/tabs";
import { LoadingSpinner } from '../ui/loading-spinner';
import { taxService } from '../../services/taxService';
import { getAuth } from 'firebase/auth';
import { format } from 'date-fns';
import TaxCalendar from './TaxCalendar';
import { Download, FileDown, CalendarIcon } from 'lucide-react';
import { useToast } from "../../hooks/use-toast";
import { getCurrentTaxYear, formatTaxYear, getTaxYearBoundaries } from '../../helpers/taxYearUtil';
import HelpIcon from '../ui/HelpIcon';
import { useAuth } from '../../hooks/useAuth';

const TaxDashboard = () => {
  const auth = getAuth();
  const { toast } = useToast();
  const { getUserProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('summary');
  const [income, setIncome] = useState('');
  const [expenses, setExpenses] = useState('');
  const [taxYear, setTaxYear] = useState('');
  const [calculatedTax, setCalculatedTax] = useState(null);
  const [taxSummary, setTaxSummary] = useState(null);
  const [includeNI, setIncludeNI] = useState(true);
  const [availableTaxYears, setAvailableTaxYears] = useState([]);
  const [error, setError] = useState(null);
  const [currentYearSummary, setCurrentYearSummary] = useState(null);
  const [currentYearLoading, setCurrentYearLoading] = useState(false);

  useEffect(() => {
    const currentTaxYearStartYear = getCurrentTaxYear();
    const currentTaxYearFormatted = formatTaxYear(currentTaxYearStartYear);
    console.log('Current tax year detected:', currentTaxYearFormatted);
    
    const years = [
      formatTaxYear(currentTaxYearStartYear),
      formatTaxYear(currentTaxYearStartYear - 1)
    ];
    
    setAvailableTaxYears(years);
    setTaxYear(currentTaxYearFormatted);
    setLoading(false);
  }, []);

  useEffect(() => {
    const loadTaxData = async () => {
      if (!auth.currentUser || !taxYear) return;
      
      setLoading(true);
      setError(null);
      
      try {
        console.log(`TaxDashboard: Loading tax data for year ${taxYear}`);
        
        // FIXED: Use taxService.calculateTaxSummary instead of reportService
        // This ensures proper deleted invoice filtering and centralized tax logic
        const taxSummaryData = await taxService.calculateTaxSummary(auth.currentUser.uid, taxYear);
        
        console.log('TaxDashboard: Tax summary data received:', taxSummaryData);
        
        if (taxSummaryData) {
          setTaxSummary(taxSummaryData);
        } else {
          console.warn('TaxDashboard: No tax summary data returned');
          setTaxSummary(null);
        }
        
      } catch (error) {
        console.error('Error loading tax data:', error);
        setError(`Failed to load tax data: ${error.message}`);
        toast({
          title: "Error",
          description: `Failed to load tax data: ${error.message}`,
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    loadTaxData();
  }, [auth.currentUser, taxYear, toast]);

  useEffect(() => {
    const loadCurrentYearSummary = async () => {
      if (!auth.currentUser) return;
      
      setCurrentYearLoading(true);
      try {
        const currentTaxYearStartYear = getCurrentTaxYear();
        const currentTaxYearFormatted = formatTaxYear(currentTaxYearStartYear);
          
        if (currentTaxYearFormatted === taxYear) {
          setCurrentYearSummary(null);
          setCurrentYearLoading(false);
          return;
        }
        
        const result = await taxService.calculateTaxSummary(auth.currentUser.uid, currentTaxYearFormatted);
        
        const processedResult = {
          ...result,
          income: parseFloat(result?.income || 0),
          expenses: parseFloat(result?.expenses || 0),
          taxableIncome: parseFloat(result?.taxableIncome || 0),
          incomeTax: parseFloat(result?.incomeTax || 0),
          nationalInsurance: parseFloat(result?.nationalInsurance || 0),
          totalTax: parseFloat(result?.totalTax || 0),
          effectiveRate: result?.effectiveRate || 0,
          taxYear: result?.taxYear || currentTaxYearFormatted,
          invoiceCount: result?.invoiceCount || 0,
          expenseCount: result?.expenseCount || 0,
          entriesCount: result?.entriesCount || 0,
          taxYearStart: result?.taxYearStart || new Date(),
          taxYearEnd: result?.taxYearEnd || new Date(),
          netIncome: parseFloat(result?.income || 0) - parseFloat(result?.totalTax || 0)
        };
        
        setCurrentYearSummary(processedResult);
      } catch (error) {
        console.error('Error loading current year summary:', error);
        setCurrentYearSummary(null);
      }
      setCurrentYearLoading(false);
    };
    
    loadCurrentYearSummary();
  }, [auth.currentUser, taxYear]);

  const handleCalculate = () => {
    // FIXED: This manual calculator now just uses the same taxService function
    // but allows users to input custom values for what-if scenarios
    if (!income || !taxYear) {
      setError('Please enter an income amount and select a tax year');
      return;
    }

    setError(null);
    
    try {
      const result = taxService.calculateTaxLiability(
        parseFloat(income) || 0,
        taxYear,
        parseFloat(expenses) || 0
      );
      
      setCalculatedTax(result);
      
    } catch (error) {
      console.error('Error calculating tax:', error);
      setError('Failed to calculate tax. Please check your inputs.');
    }
  };

  const formatCurrency = (amount) => {
    try {
      if (Array.isArray(amount)) {
        console.warn('Attempted to format an array as currency:', amount);
        let sum = 0;
        try {
          sum = amount.reduce((total, item) => {
            const itemValue = item && typeof item === 'object' && 'totalAmount' in item ? 
              parseFloat(item.totalAmount) || 0 : 0;
            return total + itemValue;
          }, 0);
        } catch (error) {
          console.error('Error summing array values:', error);
        }
        amount = sum;
      }
      
      let numAmount;
      
      if (amount !== undefined && amount !== null) {
        numAmount = Number(amount);
        
        if (isNaN(numAmount)) {
          console.warn(`Currency formatting received NaN value: ${amount}, using 0 instead`);
          numAmount = 0;
        }
      } else {
        numAmount = 0;
      }
      
      return new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: 'GBP'
      }).format(numAmount);
      
    } catch (error) {
      console.error('Error in formatCurrency:', error, 'value was:', amount);
      return "£0.00";
    }
  };

  const generateTaxReport = async () => {
    if (!taxSummary) {
      toast({
        title: "No Data",
        description: "No tax data available to generate report",
        variant: "destructive",
      });
      return;
    }

    try {
      // Get user profile information for the PDF
      let userInfo = {
        displayName: auth.currentUser?.displayName || '',
        email: auth.currentUser?.email || '',
      };
      
      // Try to get additional user info from profile
      if (getUserProfile && auth.currentUser?.uid) {
        try {
          const userProfile = await getUserProfile(auth.currentUser.uid);
          if (userProfile) {
            // Merge profile data into userInfo
            userInfo = {
              ...userInfo,
              displayName: userProfile.name || userProfile.displayName || userInfo.displayName,
              email: userProfile.email || userInfo.email,
              phone: userProfile.phone || '',
              // Build address from profile components
              address: [
                userProfile.address1,
                userProfile.address2,
                userProfile.address3,
                userProfile.address4,
                userProfile.city,
                userProfile.postcode
              ].filter(line => line && line.trim()).join('\n')
            };
          }
        } catch (profileError) {
          console.warn('Could not load user profile for PDF:', profileError);
          // Continue with basic auth info
        }
      }
      
      // FIXED: Use correct property names for tax report generation
      const doc = taxService.generateTaxReport(
        taxSummary,
        taxSummary.invoices || [], // Use invoices from summary
        taxSummary.expenseRecords || [],  // FIXED: Use expenseRecords instead of expenses
        userInfo // Pass user information for PDF header
      );
      
      if (!doc) {
        throw new Error("Failed to generate PDF document");
      }
      
      const fileName = `tax_summary_${taxSummary.taxYear}_${format(new Date(), 'yyyyMMdd')}.pdf`;
      doc.save(fileName);
      
      toast({
        title: "Report Generated",
        description: `Your tax report has been downloaded as "${fileName}"`,
        variant: "success",
      });
    } catch (error) {
      console.error('Error generating tax report:', error);
      toast({
        title: "Error",
        description: "Failed to generate report: " + (error.message || "Unknown error"),
        variant: "destructive",
      });
    }
  };

  const formatTaxYearPeriod = (start, end) => {
    if (!start || !end) return "Invalid period";
    
    try {
      const startDate = start instanceof Date ? start : new Date(start);
      const endDate = end instanceof Date ? end : new Date(end);
      
      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
        return "Invalid date range";
      }
      
      return `${format(startDate, 'd MMM yyyy')} - ${format(endDate, 'd MMM yyyy')}`;
    } catch (error) {
      console.error('Error formatting tax year period:', error);
      return "Error formatting period";
    }
  };

  return (
    <div className="container mx-auto px-4 py-6">
      {/* Add HelpIcon next to the title */}
      <div className="flex items-center gap-2 mb-4">
        <h1 className="text-xl font-bold text-accent-foreground">Tax Calculator</h1>
        <HelpIcon sectionKey="TaxDashboard" />
      </div>

      <Card className="app-card-bg border-border shadow-lg mb-6">
        <CardContent className="p-4">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-3">
            <TabsList className="grid grid-cols-3 gap-2 app-bg-secondary p-1 rounded-lg">
              <TabsTrigger 
                value="summary" 
                className="text-muted-foreground data-[state=active]:app-accent-purple-bg data-[state=active]:!text-white text-sm"
              >
                Tax Summary
              </TabsTrigger>
              <TabsTrigger 
                value="calculator" 
                className="text-muted-foreground data-[state=active]:app-accent-purple-bg data-[state=active]:!text-white text-sm"
              >
                Calculator
              </TabsTrigger>
              <TabsTrigger 
                value="calendar" 
                className="text-muted-foreground data-[state=active]:app-accent-purple-bg data-[state=active]:!text-white text-sm"
              >
                Deadlines
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="summary" className="pt-3">
              <div className="flex justify-between items-center mb-4">
                <p className="text-muted-foreground text-sm">
                  This summary shows your estimated tax position based on invoices and expenses recorded in the system.
                </p>
                
                {taxSummary && !loading && (
                  <Button 
                    onClick={generateTaxReport} 
                    className="app-accent-purple-bg hover:app-accent-purple-hover text-white text-xs flex items-center"
                    size="sm"
                  >
                    <FileDown className="h-3.5 w-3.5 mr-1" />
                    Download Tax Report
                  </Button>
                )}
              </div>
              
              {currentYearSummary && currentYearSummary.taxYear !== taxYear && 
               currentYearSummary.taxYearStart && currentYearSummary.income !== undefined && 
               (currentYearSummary.invoiceCount > 0 || currentYearSummary.expenseCount > 0) && (
                <div className="mb-4 app-accent-purple-bg-light border border-border rounded-lg p-3"> 
                  <h4 className="font-medium text-accent-foreground text-sm mb-1 flex items-center"> 
                    <CalendarIcon className="h-3.5 w-3.5 mr-1.5" />
                    Current Tax Year ({currentYearSummary.taxYear}) Summary To Date
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                    <div>
                      <span className="text-muted-foreground">Income:</span>
                      <span className="text-primary ml-1 font-medium">{formatCurrency(currentYearSummary.income)}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Expenses:</span>
                      <span className="text-primary ml-1 font-medium">{formatCurrency(currentYearSummary.expenses)}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Tax Estimate:</span>
                      <span className="text-primary ml-1 font-medium">{formatCurrency(currentYearSummary.totalTax)}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Period:</span>
                      <span className="text-primary ml-1 font-medium">
                        {format(
                          currentYearSummary.taxYearStart instanceof Date ? 
                            currentYearSummary.taxYearStart : 
                            new Date(), 
                          'dd/MM/yy'
                        )} - Today
                      </span>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="mb-4">
                <Label className="text-accent-foreground mb-1 block text-sm">Tax Year</Label>
                <Select value={taxYear} onValueChange={setTaxYear}>
                  <SelectTrigger className="w-full md:w-48 app-input-bg border-input text-primary text-sm">
                    <SelectValue placeholder="Select Year" />
                  </SelectTrigger>
                  <SelectContent className="app-card-bg border-border">
                    {availableTaxYears.map((year) => (
                      <SelectItem 
                        key={year} 
                        value={year}
                        className={`text-primary hover:bg-accent text-sm ${year === currentYearSummary?.taxYear ? 'font-bold' : ''}`}
                      >
                        {year} {year === currentYearSummary?.taxYear && '(Current)'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {loading ? (
                <div className="flex justify-center p-8">
                  <LoadingSpinner size="large" />
                </div>
              ) : error ? (
                <div className="bg-red-500/10 border border-red-500/30 p-4 rounded-lg text-center text-sm">
                  <p className="text-red-500 dark:text-red-400">
                    {error}
                  </p>
                </div>
              ) : taxSummary && (taxSummary.invoiceCount > 0 || taxSummary.expenseCount > 0) ? (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="light:app-accent-purple-bg-light dark:app-bg-secondary p-4 rounded-lg">
                      <h3 
                        className="text-base font-medium mb-3 border-b border-border/50 pb-2" 
                        style={{ color: 'var(--app-accent-purple)' }}
                      >
                        Financial Summary
                      </h3>
                      
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Total Income:</span>
                          <span className="text-primary font-medium">
                            {formatCurrency(taxSummary.income)}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Total Expenses:</span>
                          <span className="text-primary font-medium">
                            {formatCurrency(taxSummary.expenses)}
                          </span>
                        </div>
                        <div className="flex justify-between border-t border-border/50 pt-2 mt-2">
                          <span className="text-muted-foreground">Taxable Income:</span>
                          <span className="text-primary font-medium">
                            {formatCurrency(taxSummary.taxableIncome)}
                          </span>
                        </div>
                      </div>
                      
                      <h3 
                        className="text-base font-medium mb-2 mt-4 border-b border-border/50 pb-2" 
                        style={{ color: 'var(--app-accent-purple)' }}
                      >
                        Tax Breakdown
                      </h3>
                      
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Income Tax:</span>
                          <span className="text-primary font-medium">{formatCurrency(taxSummary.incomeTax)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">National Insurance:</span>
                          <span className="text-primary font-medium">{formatCurrency(taxSummary.nationalInsurance)}</span>
                        </div>
                        <div className="flex justify-between border-t border-border/50 pt-2 mt-2">
                          <span className="text-muted-foreground font-medium">Total Tax:</span>
                          <span className="text-primary font-bold">{formatCurrency(taxSummary.totalTax)}</span>
                        </div>
                      </div>
                      
                      <div className="mt-4 pt-3 border-t border-border/50">
                        <div className="flex justify-between text-sm">
                          <span className="text-accent-foreground font-medium">Net Income (After Tax):</span>
                          <span className="text-accent-foreground font-bold">{formatCurrency(taxSummary.netIncome)}</span>
                        </div>
                        <div className="text-xs text-muted-foreground mt-1 text-right">
                          Effective Tax Rate: {taxSummary.effectiveRate}% 
                        </div>
                      </div>
                    </div>
                    
                    <div className="light:app-accent-purple-bg-light dark:app-bg-secondary p-4 rounded-lg space-y-3 text-sm">
                      <div>
                        <h3 className="text-base font-medium text-accent-foreground mb-3 border-b border-border/50 pb-2">
                          Tax Year Period
                        </h3>
                        <p className="text-muted-foreground">
                          <span className="font-medium text-primary">From:</span> {taxSummary.taxYearStart ? 
                            format(new Date(taxSummary.taxYearStart), 'd MMM yyyy') : 'Loading...'} <br />
                          <span className="font-medium text-primary">To:</span> {taxSummary.taxYearEnd ? 
                            format(new Date(taxSummary.taxYearEnd), 'd MMM yyyy') : 'Loading...'}
                        </p>
                        <p className="text-xs text-accent-foreground mt-2">
                          UK Tax Year: {taxSummary.taxYear}
                        </p>
                      </div>
                      
                      <div>
                        <h3 className="text-base font-medium text-accent-foreground mb-2 border-b border-border/50 pb-2">
                          Data Summary
                        </h3>
                        <ul className="space-y-1">
                          <li className="text-muted-foreground">
                            <span className="font-medium text-primary">Invoices:</span> {taxSummary.invoiceCount} recorded
                          </li>
                          <li className="text-muted-foreground">
                            <span className="font-medium text-primary">Invoice Entries:</span> {taxSummary.entriesCount || '?'} within tax year
                          </li>
                          <li className="text-muted-foreground">
                            <span className="font-medium text-primary">Expenses:</span> {taxSummary.expenseCount} recorded
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div className="app-bg-secondary p-4 rounded-lg text-center text-sm">
                  <p className="text-muted-foreground">
                    No data available for the selected tax year.
                  </p>
                </div>
              )}
            </TabsContent>
            
            <TabsContent value="calculator" className="pt-3">
              <p className="text-muted-foreground mb-4 text-sm">
                Use this calculator to estimate your self-employed tax liability.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <div className="mb-3">
                    <Label htmlFor="tax-year" className="text-accent-foreground mb-1 block text-sm">Tax Year</Label>
                    <Select value={taxYear} onValueChange={setTaxYear}>
                      <SelectTrigger className="w-full app-input-bg border-input text-primary text-sm">
                        <SelectValue placeholder="Select Year" />
                      </SelectTrigger>
                      <SelectContent className="app-card-bg border-border">
                        {availableTaxYears.map((year) => (
                          <SelectItem key={year} value={year} className="text-primary hover:bg-accent text-sm">
                            {year}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="mb-3">
                    <Label htmlFor="income" className="text-accent-foreground mb-1 block text-sm">Income (£)</Label>
                    <Input
                      id="income"
                      type="number"
                      placeholder="Enter your annual income"
                      value={income}
                      onChange={(e) => setIncome(e.target.value)}
                      className="text-primary placeholder:text-muted-foreground text-sm h-9"
                    />
                  </div>
                  
                  <div className="mb-3">
                    <Label htmlFor="expenses" className="text-accent-foreground mb-1 block text-sm">Expenses (£)</Label>
                    <Input
                      id="expenses"
                      type="number"
                      placeholder="Enter your annual expenses"
                      value={expenses}
                      onChange={(e) => setExpenses(e.target.value)}
                      className="text-primary placeholder:text-muted-foreground text-sm h-9"
                    />
                  </div>
                  
                  <div className="mb-3 flex items-center">
                    <input
                      type="checkbox"
                      id="includeNI"
                      checked={includeNI}
                      onChange={() => setIncludeNI(!includeNI)}
                      className="mr-2 h-3.5 w-3.5"
                    />
                    <Label htmlFor="includeNI" className="text-accent-foreground text-sm">Include National Insurance</Label>
                  </div>
                  
                  <Button 
                    onClick={handleCalculate}
                    className="w-full app-accent-purple-bg text-white hover:app-accent-purple-hover text-sm py-1.5"
                  >
                    Calculate
                  </Button>
                </div>
                
                <div className="app-bg-secondary p-4 rounded-lg">
                  <h3 className="text-base font-medium text-accent-foreground mb-3">Tax Calculation Results</h3>
                  
                  {calculatedTax ? (
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between border-b border-border/50 pb-1.5">
                        <span className="text-muted-foreground">Income:</span>
                        <span className="text-primary font-medium">{formatCurrency(calculatedTax.income)}</span>
                      </div>
                      <div className="flex justify-between border-b border-border/50 pb-1.5">
                        <span className="text-muted-foreground">Expenses:</span>
                        <span className="text-primary font-medium">{formatCurrency(calculatedTax.expenses)}</span>
                      </div>
                      <div className="flex justify-between border-b border-border/50 pb-1.5">
                        <span className="text-muted-foreground">Taxable Income:</span>
                        <span className="text-primary font-medium">{formatCurrency(calculatedTax.taxableIncome)}</span>
                      </div>
                      <div className="flex justify-between border-b border-border/50 pb-1.5">
                        <span className="text-muted-foreground">Income Tax:</span>
                        <span className="text-primary font-medium">{formatCurrency(calculatedTax.incomeTax)}</span>
                      </div>
                      {includeNI && (
                        <div className="flex justify-between border-b border-border/50 pb-1.5">
                          <span className="text-muted-foreground">National Insurance:</span>
                          <span className="text-primary font-medium">{formatCurrency(calculatedTax.nationalInsurance)}</span>
                        </div>
                      )}
                      <div className="flex justify-between pt-1.5">
                        <span className="text-accent-foreground font-medium">Total Tax Due:</span>
                        <span className="text-accent-foreground font-bold">
                          {formatCurrency(includeNI ? calculatedTax.totalTax : calculatedTax.incomeTax)}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <p className="text-muted-foreground text-center py-4 text-sm">
                      Enter your income and expenses to calculate your tax liability.
                    </p>
                  )}
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="calendar" className="pt-3">
              <TaxCalendar />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default TaxDashboard;
